import React, { useEffect, useRef, useState } from 'react'
import { toast } from '@CommonComponents/toast'
import { notebook as endpoints } from '@URLS'
import { client } from '@Utils'
import { useStateValue } from './state'
import { PillsContainer } from './FilterPills'
import * as styles from './notebook_tool.module.scss'

export const SearchForm = () => {
  const [currentState, dispatch] = useStateValue()
  const [showFilters, setShowFilters] = useState(false)
  const currentStateRef = useRef(currentState)
  const isFirstFilterRender = useRef(true)

  useEffect(() => {
    currentStateRef.current = currentState
  })

  const pageIdRef = useRef(null)
  if (pageIdRef.current === null) {
    const el = document.querySelector('#bookcontent > div[id^="section_"]')
    pageIdRef.current = el ? el.dataset.id : null
  }
  const pageId = pageIdRef.current
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await client.get(
          `/reader/notebook.get_initial_data/${currentState.coursenum}/${pageId}/`
        )
        dispatch({
          type: 'updatePageID',
          payload: pageId,
        })
        dispatch({
          type: 'updateHighlights',
          payload: resp.data.highlights,
        })
        dispatch({
          type: 'updateColors',
          payload: resp.data.highlightColors,
        })
        dispatch({
          type: 'updateChapters',
          payload: resp.data.chapters,
        })
        dispatch({
          type: 'updateTags',
          payload: resp.data.tags,
        })
        dispatch({
          type: 'updateHighlightTotal',
          payload: resp.data.total,
        })
        dispatch({
          type: 'updateHasMore',
          payload: resp.data.highlights.length < resp.data.total,
        })
      } catch (error) {
        console.error('Error:', error)
      }
    }

    fetchData()
  }, [pageId, currentState.coursenum])

  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false
      return
    }
    const fetchFilteredHighlights = async () => {
      let data = {
        filters: currentState.filters,
        pageId: currentState.pageId,
      }
      try {
        let resp = await client.post(endpoints.filterHighlights(), data)
        if (!resp.data || !Array.isArray(resp.data.highlights)) {
          toast.error('Failed to load highlights. Please try again.')
          return
        }
        dispatch({ type: 'updateHighlights', payload: resp.data.highlights })
        dispatch({ type: 'updateMessage', payload: resp.data.message })
        dispatch({ type: 'updateHighlightTotal', payload: resp.data.total })
        dispatch({
          type: 'updateHasMore',
          payload: resp.data.highlights.length < resp.data.total,
        })
      } catch (err) {
        toast.error(
          (err.response && err.response.data && err.response.data.error) ||
            String(err)
        )
      }
    }

    fetchFilteredHighlights()
  }, [currentState.filters])

  useEffect(() => {
    const refetchHighlights = async () => {
      const state = currentStateRef.current
      try {
        const resp = await client.post(endpoints.filterHighlights(), {
          ...state,
          filters: state.filters,
        })
        dispatch({ type: 'updateHighlights', payload: resp.data.highlights })
        dispatch({ type: 'updateMessage', payload: resp.data.message })
        dispatch({ type: 'updateHighlightTotal', payload: resp.data.total })
        dispatch({
          type: 'updateHasMore',
          payload: resp.data.highlights.length < resp.data.total,
        })
        const existingTagIds = new Set(state.tags.map(t => t[0]))
        const newTagsMap = new Map()
        for (const hl of resp.data.highlights) {
          for (const tag of hl.tags || []) {
            if (!existingTagIds.has(tag[0]) && !newTagsMap.has(tag[0])) {
              newTagsMap.set(tag[0], tag)
            }
          }
        }
        if (newTagsMap.size > 0) {
          dispatch({
            type: 'updateTags',
            payload: [...state.tags, ...newTagsMap.values()],
          })
        }
      } catch (err) {
        // silently fail — the reader already shows the user any errors
      }
    }

    const handleHighlightDeleted = e => {
      const { highlightId } = e.detail
      const state = currentStateRef.current
      dispatch({
        type: 'updateHighlights',
        payload: state.highlights.filter(
          h => String(h.hId) !== String(highlightId)
        ),
      })
    }

    window.addEventListener('highlightCreated', refetchHighlights)
    window.addEventListener('highlightUpdated', refetchHighlights)
    window.addEventListener('highlightDeleted', handleHighlightDeleted)

    return () => {
      window.removeEventListener('highlightCreated', refetchHighlights)
      window.removeEventListener('highlightUpdated', refetchHighlights)
      window.removeEventListener('highlightDeleted', handleHighlightDeleted)
    }
  }, []) // set up once on mount; uses currentStateRef for fresh state

  const updateSearch = val => {
    dispatch({
      type: 'updateFilters',
      payload: { ...currentState.filters, search: val },
    })
  }

  const updateFilters = filterType => async evt => {
    const currentValues = currentState.filters[filterType]
    if (!currentValues.includes(evt.target.value)) {
      dispatch({
        type: 'updateFilters',
        payload: {
          ...currentState.filters,
          [filterType]: [...currentValues, evt.target.value],
        },
      })
    }
  }

  const searchValue =
    (currentState.filters && currentState.filters.search) || ''

  const hasActivePills =
    currentState.filters.chapters.length > 0 ||
    currentState.filters.tags.length > 0 ||
    currentState.filters.colors.length > 0

  return (
    <div>
      <div
        className={`${styles.searchRow} w-100 d-flex align-items-center bg-white`}
      >
        <i
          className={`fa-solid fa-magnifying-glass ${styles.searchIcon}`}
          aria-hidden="true"
        />

        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search highlights, tags..."
          aria-label="Search"
          value={searchValue}
          onChange={e => updateSearch(e.target.value)}
        />

        <button
          type="button"
          className={`${styles.filterButton}${
            showFilters ? ` ${styles.filterButtonActive}` : ' bg-transparent'
          } ml-2 cursor-pointer border-0`}
          aria-label="Open search filters"
          onClick={() => setShowFilters(s => !s)}
        >
          <i className={`fa-solid fa-bars-filter ${styles.filterIcon}`}></i>
        </button>
      </div>

      <div
        className={`${styles.filterAccordion} ${
          showFilters ? `${styles.open} border-me-lightgray` : ''
        }${showFilters && !hasActivePills ? ' border-bottom' : ''}`}
        aria-hidden={!showFilters}
        inert={!showFilters ? '' : undefined}
      >
        <div className={`${styles.filterAccordionInner} overflow-hidden`}>
          <div className={`${styles.filterForm} mx-auto`}>
            <div
              className={`${styles.filterFormGroup} d-flex flex-column flex-md-row align-items-md-center mb-3`}
            >
              <label
                htmlFor="chapterFilter"
                className={`${styles.filterLabel} d-block mb-1`}
              >
                <b>Chapter:</b>
              </label>
              <select
                className={`${styles.filterControl} form-control`}
                aria-label="Chapter"
                id="chapterFilter"
                value=""
                onChange={updateFilters('chapters')}
              >
                <option value="">-Select Chapters-</option>
                {currentState.chapters.map((chap, i) => (
                  <option value={chap[0]} key={i}>
                    {chap[1]}
                  </option>
                ))}
              </select>
            </div>

            <div
              className={`${styles.filterFormGroup} d-flex flex-column flex-md-row align-items-md-center mb-3`}
            >
              <label
                htmlFor="colorFilter"
                className={`${styles.filterLabel} d-block mb-1`}
              >
                <b>Color:</b>
              </label>
              <select
                className={`${styles.filterControl} form-control`}
                aria-label="Color"
                value=""
                onChange={updateFilters('colors')}
                id="colorFilter"
              >
                <option value="">-Select Colors-</option>
                <optgroup label="Highlighted">
                  {currentState.colors.length > 0 &&
                    currentState.colors
                      .filter(color => !color[0].includes('underline'))
                      .map((color, i) => (
                        <option value={color[0]} key={i}>
                          {color[1]}
                        </option>
                      ))}
                </optgroup>
                <optgroup label="Underlined">
                  {currentState.colors.length > 0 &&
                    currentState.colors
                      .filter(color => color[0].includes('underline'))
                      .map((color, i) => (
                        <option value={color[0]} key={i}>
                          {color[1]}
                        </option>
                      ))}
                </optgroup>
              </select>
            </div>

            <div
              className={`${styles.filterFormGroup} d-flex flex-column flex-md-row align-items-md-center mb-3`}
            >
              <label
                htmlFor="tagFilter"
                className={`${styles.filterLabel} d-block mb-1`}
              >
                <b>Tag:</b>
              </label>
              <select
                className={`${styles.filterControl} form-control`}
                aria-label="Tag"
                value=""
                onChange={updateFilters('tags')}
                id="tagFilter"
              >
                <option value="">-Select Tags-</option>
                {currentState.tags.map(tag => (
                  <option key={tag[0]} value={tag[0]}>
                    {tag[1]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className={hasActivePills ? 'border-bottom border-me-lightgray' : undefined}>
        <PillsContainer />
      </div>
    </div>
  )
}
